import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getMachineTypeLabel } from "@/lib/display-labels";
import { useModels3d } from "@/features/models-3d/hooks";
import type { BasicForm } from "./types";

interface IdentitySectionProps {
  form: BasicForm;
  isView: boolean;
  patch: <K extends keyof BasicForm>(key: K, value: BasicForm[K]) => void;
}

export function IdentitySection({ form, isView, patch }: IdentitySectionProps) {
  // Spec 078 — o modelo 3D é escolhido aqui (uma calibração serve todas as máquinas do modelo).
  const models = useModels3d();
  const modelName = models.data?.find((m) => m.id === form.model3dId)?.name;
  return (
    <div className="grid gap-3">
      <div className="space-y-1.5">
        <Label htmlFor="md-code">Código</Label>
        {isView ? (
          <p className="font-mono text-sm">{form.code || "—"}</p>
        ) : (
          <Input
            id="md-code"
            value={form.code}
            onChange={(e) => patch("code", e.target.value)}
            maxLength={40}
            placeholder="Ex.: M-01"
          />
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="md-name">Nome</Label>
        {isView ? (
          <p className="text-sm">{form.name || "—"}</p>
        ) : (
          <Input
            id="md-name"
            value={form.name}
            onChange={(e) => patch("name", e.target.value)}
            maxLength={120}
          />
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="md-type">Tipo</Label>
        {isView ? (
          <p className="text-sm">{getMachineTypeLabel(form.type)}</p>
        ) : (
          <select
            id="md-type"
            value={form.type}
            onChange={(e) => patch("type", e.target.value as BasicForm["type"])}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
          >
            <option value="MACHINE">Máquina</option>
            <option value="EQUIPMENT">Equipamento</option>
            <option value="HUMAN">Humano</option>
          </select>
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="md-cost">Custo de parada por hora (R$)</Label>
        {isView ? (
          <p className="font-mono text-sm">
            {form.stoppedCostPerHour ? `R$ ${Number(form.stoppedCostPerHour).toFixed(2)}` : "—"}
          </p>
        ) : (
          <Input
            id="md-cost"
            type="number"
            min="0"
            step="0.01"
            placeholder="0"
            value={form.stoppedCostPerHour}
            onChange={(e) => patch("stoppedCostPerHour", e.target.value)}
          />
        )}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          id="md-reqop"
          type="checkbox"
          checked={form.requiresOperator}
          disabled={isView}
          onChange={(e) => patch("requiresOperator", e.target.checked)}
        />
        Exige operador presente
      </label>
      <div className="space-y-1.5">
        <Label htmlFor="md-model3d">Modelo 3D</Label>
        {isView ? (
          <p className="text-sm">{modelName ?? "Bloco genérico"}</p>
        ) : (
          <select
            id="md-model3d"
            value={form.model3dId}
            onChange={(e) => patch("model3dId", e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
          >
            <option value="">Nenhum — bloco genérico na cena</option>
            {(models.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        )}
        <p className="text-xs text-text-tertiary">
          A calibração (apoio, entrada e saída) vem do modelo e vale para toda máquina que o usa.
        </p>
      </div>
    </div>
  );
}
